import { useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import HiddenEMSReport from "./HiddenEMSReport";
import Acumen_25_Years_LOGO from "../assets/Acumen_25_Years_LOGO.png";

// ─── DESIGN TOKENS ────────────────────────────────────────────────────────────
const T = {
  navy:  "#A80D2C",
  blue:  "#C4163A",
  teal:  "#7B0F2B",
  gold:  "#D79A1E",
  slate: "#5B677A",
  bg:    "#F5F8FC",
  card:  "#FFFFFF",
  line:  "#DCE5EF",
  ink:   "#1D2733",
};

// ─── Per-style accent colours ─────────────────────────────────────────────────
const STYLE_COLORS = {
  authoritative: { color: "#1e5fa8", bg: "#f0f6ff", gradient: "linear-gradient(135deg,#1e3a8a,#1e5fa8)" },
  coercer:       { color: "#b91c1c", bg: "#fff5f5", gradient: "linear-gradient(135deg,#7f1d1d,#b91c1c)" },
  democrat:      { color: "#0d7a6e", bg: "#f0fdf9", gradient: "linear-gradient(135deg,#064e3b,#0d7a6e)" },
  affiliator:    { color: "#7c3aed", bg: "#faf5ff", gradient: "linear-gradient(135deg,#4c1d95,#7c3aed)" },
  pacesetter:    { color: "#c2570b", bg: "#fff7ed", gradient: "linear-gradient(135deg,#7c2d12,#c2570b)" },
  coach:         { color: "#0e6ea8", bg: "#f0f9ff", gradient: "linear-gradient(135deg,#0c4a6e,#0e6ea8)" },
};

// ─── STATIC STYLE DEFINITIONS ─────────────────────────────────────────────────
const STYLE_DEFINITIONS = [
  {
    key: "authoritative",
    label: "Authoritative",
    tagline: '"Come with me!"',
    overview: "You provide clear, long-term direction and vision. People know exactly where they are going. This is highly effective when a team needs a new vision or clear guidance.",
    execFrame: "Focuses on mobilizing people toward a shared vision. This style provides strategic clarity and long-term direction, making it highly effective during periods of organizational transformation, turnarounds, or when establishing a new strategic course.",
    strengths: "Creates clarity, inspires commitment, and aligns effort around purpose.",
    weaknesses: "Can become overly directive if used in situations that require more participation or autonomy.",
    bestUsed: "A new direction is needed, during change, or when a team needs clarity and inspiration.",
  },
  {
    key: "coercer",
    label: "Coercer",
    tagline: '"Do what I tell you!"',
    overview: "You demand immediate compliance and obedience. This style is incredibly effective in a crisis or emergency, but if used too often, it can hurt team morale.",
    execFrame: "Demands immediate compliance and operates with unilateral decision-making. While over-reliance on this style can degrade organizational climate, it remains a critical asset during crises, genuine emergencies, or when dealing with highly problematic performers.",
    strengths: "Creates immediate control, establishes order, and is effective in urgent situations.",
    weaknesses: "Can damage trust, reduce engagement, and suppress initiative when overused.",
    bestUsed: "Quick action is essential, especially in a crisis or when dealing with repeated non-performance.",
  },
  {
    key: "democrat",
    label: "Democrat",
    tagline: '"What do you think?"',
    overview: "You build consensus by getting everyone's input. This is the best style to use when you need buy-in from your team or when you want to generate fresh ideas.",
    execFrame: "Leverages collective intelligence by forging consensus through participation. This style is optimal for driving team alignment, generating innovative solutions, and navigating complex matrixed stakeholder environments.",
    strengths: "Encourages participation, improves buy-in, and often leads to better ideas through shared thinking.",
    weaknesses: "Can slow decisions when speed matters or when too many viewpoints create delay.",
    bestUsed: "Consensus is important, or when a leader wants to draw on team expertise and engagement.",
  },
  {
    key: "affiliator",
    label: "Affiliator",
    tagline: '"People come first."',
    overview: "You focus on creating harmony and building emotional bonds. This is an excellent style for healing team conflicts or motivating people during stressful times.",
    execFrame: null,
    strengths: "Builds trust, strengthens morale, and helps people feel valued and connected.",
    weaknesses: "Can avoid tough conversations or underperformance if harmony is prioritised over accountability.",
    bestUsed: "A team needs healing, emotional support, or a stronger sense of belonging.",
  },
  {
    key: "pacesetter",
    label: "Pacesetter",
    tagline: '"Do as I do, right now!"',
    overview: "You set extremely high standards for performance and lead by example. This works great with highly motivated, competent teams, but can overwhelm others.",
    execFrame: null,
    strengths: "Drives pace, raises performance standards, and creates momentum.",
    weaknesses: "Can create pressure, reduce morale, and increase burnout if used too often or without support.",
    bestUsed: "The team is already skilled and motivated, and performance needs to be raised quickly.",
  },
  {
    key: "coach",
    label: "Coach",
    tagline: '"Try this."',
    overview: "You focus heavily on developing people for the future. This style is perfect for helping employees improve their weaknesses and build long-term strengths.",
    execFrame: null,
    strengths: "Develops capability, strengthens future readiness, and supports long-term growth.",
    weaknesses: "Requires time, patience, and strong coaching skill; may be less effective when urgency is high.",
    bestUsed: "The goal is to build future strength, improve performance, or support long-term development.",
  },
];

const DRIVER_DEFINITIONS = [
  {
    key: "power",
    label: "Power",
    desc: "You are energized by the desire to influence others, take charge, and make a strong impact on your organization. At the executive level, this translates to a drive to influence outcomes, architect systemic change, and take decisive charge of organizational direction.",
  },
  {
    key: "affiliation",
    label: "Affiliation",
    desc: "You are energized by building strong, friendly, and close interpersonal relationships with your team. Executives with this driver are motivated by cultivating robust, collaborative networks and fostering a deeply cohesive, supportive team culture.",
  },
  {
    key: "achievement",
    label: "Achievement",
    desc: "You are energized by overcoming challenges, hitting goals, and excelling against high standards. At the executive level, this reflects a continuous pursuit of excellence, overcoming complex business challenges, and consistently exceeding established performance benchmarks.",
  },
];

// ─── RADAR CHART ──────────────────────────────────────────────────────────────
function RadarChart({ data, loading }) {
  const size = 310;
  const cx = size / 2; const cy = size / 2;
  const r = 90; const levels = 5; const maxVal = 10; const n = data.length;

  const angleOf = i => (Math.PI * 2 * i) / n - Math.PI / 2;
  const pointAt = (angle, radius) => ({ x: cx + radius * Math.cos(angle), y: cy + radius * Math.sin(angle) });

  const gridPolygons = Array.from({ length: levels }, (_, lvl) => {
    const frac = (lvl + 1) / levels;
    return data.map((_, i) => { const p = pointAt(angleOf(i), r * frac); return `${p.x},${p.y}`; }).join(" ");
  });

  const dataPoints = loading ? null
    : data.map(({ score }, i) => { const p = pointAt(angleOf(i), r * ((score ?? 0) / maxVal)); return `${p.x},${p.y}`; }).join(" ");

  const axes = data.map((_, i) => { const end = pointAt(angleOf(i), r); return { x1: cx, y1: cy, x2: end.x, y2: end.y }; });

  const labels = data.map(({ def, score }, i) => {
    const angle = angleOf(i);
    const lp = pointAt(angle, r + 22);
    const anchor = Math.abs(lp.x - cx) < 6 ? "middle" : lp.x < cx ? "end" : "start";
    return { x: lp.x, y: lp.y, label: def.label, score, anchor };
  });

  return (
    <svg viewBox={`0 0 ${size} ${size}`} width="100%" style={{ maxWidth: size, display: "block", margin: "0 auto" }}>
      {gridPolygons.map((pts, i) => <polygon key={i} points={pts} fill="none" stroke="#DCE5EF" strokeWidth={i === levels - 1 ? 1.5 : 1} />)}
      {axes.map((a, i) => <line key={i} x1={a.x1} y1={a.y1} x2={a.x2} y2={a.y2} stroke="#DCE5EF" strokeWidth={1} />)}
      {!loading && dataPoints && (
        <>
          <polygon points={dataPoints} fill={`${T.navy}28`} stroke={T.navy} strokeWidth={2} strokeLinejoin="round" />
          {data.map(({ score }, i) => {
            const p = pointAt(angleOf(i), r * ((score ?? 0) / maxVal));
            return <circle key={i} cx={p.x} cy={p.y} r={4} fill={T.blue} stroke="#fff" strokeWidth={1.5} />;
          })}
        </>
      )}
      {labels.map(({ x, y, label, score, anchor }, i) => (
        <g key={i}>
          <text x={x} y={y - 4} textAnchor={anchor} fontSize="9" fontWeight="700" fill={T.slate} style={{ textTransform: "uppercase" }}>{label.toUpperCase()}</text>
          {!loading && score != null && <text x={x} y={y + 9} textAnchor={anchor} fontSize="11" fontWeight="700" fill={T.navy}>{score}</text>}
        </g>
      ))}
      {loading && <polygon points={gridPolygons[2]} fill="#EAF0F6" stroke="none" opacity={0.6} />}
    </svg>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
export default function EMSResult() {
  const location = useLocation();
  const navState = location.state || {};

  const [reportMeta,   setReportMeta]   = useState(null);
  const [styleScores,  setStyleScores]  = useState(null);
  const [driverScores, setDriverScores] = useState(null);
  const [insightText,  setInsightText]  = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [pdfData,      setPdfData]      = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [openStyleKey, setOpenStyleKey] = useState(null);
  const generatingRef = useRef(false);

  // ── PDF generation ─────────────────────────────────────────────────────────
  const handleDownloadPDF = () => {
    if (generatingRef.current) return;
    generatingRef.current = true;
    setIsGenerating(true);
    setPdfData({
      userInfo:     { name: reportMeta?.orgName || "Participant" },
      styleScores:  styleScores  || {},
      driverScores: driverScores || {},
      insightText:  insightText  || "",
      submission_time: navState.scores?.submission_time ?? null,
    });
  };

  const handlePdfComplete = useCallback(() => {
    setPdfData(null);
    setIsGenerating(false);
    generatingRef.current = false;
  }, []);

  // ── Data loading ───────────────────────────────────────────────────────────
  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);

        if (navState.scores) {
          const emsData = navState.scores;
          const ls = emsData.leadership_styles;
          const md = emsData.motivational_drivers;

          const styles = {
            authoritative: ls.authoritarian?.adjusted ?? 0,
            coercer:       ls.coercer?.adjusted       ?? 0,
            democrat:      ls.democrat?.adjusted       ?? 0,
            affiliator:    ls.affiliator?.adjusted     ?? 0,
            pacesetter:    ls.pacesetter?.adjusted     ?? 0,
            coach:         ls.coach?.adjusted          ?? 0,
          };

          const drivers = {
            power:       md.power       ?? 0,
            affiliation: md.affiliation ?? 0,
            achievement: md.achievement ?? 0,
          };

          const topStyleKey  = Object.entries(styles).sort((a, b) => b[1] - a[1])[0]?.[0];
          const topDriverKey = Object.entries(drivers).sort((a, b) => b[1] - a[1])[0]?.[0];
          const topStyleLabel  = STYLE_DEFINITIONS.find(s => s.key === topStyleKey)?.label  ?? topStyleKey;
          const topDriverLabel = DRIVER_DEFINITIONS.find(d => d.key === topDriverKey)?.label ?? topDriverKey;

          const dateStr = emsData.submission_time
            ? new Date(emsData.submission_time).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })
            : new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

          setReportMeta({
            orgName:     navState.name || "Participant",
            period:      dateStr,
            reportTitle: "Leadership Styles Assessment",
          });
          setStyleScores(styles);
          setDriverScores(drivers);
          setInsightText(`Your highest-scoring leadership style is ${topStyleLabel}, and your dominant motivational driver is ${topDriverLabel}. These two dimensions work together to shape how you lead — ${topStyleLabel} defines your day-to-day management approach, while ${topDriverLabel} reflects what energises you at a deeper level.`);
          return;
        }

        const email = navState.email ?? null;
        const phone = navState.phone ?? null;
        if (!email && !phone) { setError("No result data found. Please complete the survey first."); return; }

        const API_BASE = import.meta.env.VITE_API_URL ?? "";
        const res = await fetch(`${API_BASE}/api/calculate-score/ems`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, phone }),
        });
        if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error ?? `Server error ${res.status}`); }
        const json = await res.json();
        if (!json.success) throw new Error(json.error ?? "Unexpected response.");

      } catch (e) {
        setError(e.message || "Failed to load report data. Please try again.");
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  // ── Derived display data ───────────────────────────────────────────────────
  const stylesForDisplay  = STYLE_DEFINITIONS.map(def => ({ def, score: styleScores?.[def.key]  ?? null }));
  const driversForDisplay = DRIVER_DEFINITIONS.map(d   => ({ def: d,  score: driverScores?.[d.key]  ?? null }));
  const maxDriver = driverScores ? Math.max(...Object.values(driverScores), 1) : 20;

  const sortedStyles = [...stylesForDisplay].sort((a, b) => (b.score ?? 0) - (a.score ?? 0));
  const dominantStyleLabel = sortedStyles[0]?.def?.label || "—";
  const secondStyleLabel   = sortedStyles[1]?.def?.label || "—";
  const dominantDriverLabel = [...driversForDisplay].sort((a, b) => (b.score ?? 0) - (a.score ?? 0))[0]?.def?.label || "—";

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="ems-report" id="ems-report-content">
        <link href="https://api.fontshare.com/v2/css?f[]=satoshi@300,400,500,700,900&display=swap" rel="stylesheet" />
        <style>{`
          :root {
            --text-xs:   clamp(0.75rem,  0.7rem  + 0.25vw, 0.875rem);
            --text-sm:   clamp(0.875rem, 0.8rem  + 0.35vw, 1rem);
            --text-base: clamp(1rem,     0.95rem + 0.25vw, 1.125rem);
            --text-lg:   clamp(1.125rem, 1rem    + 0.75vw, 1.4rem);
            --text-xl:   clamp(1.45rem,  1.2rem  + 1.1vw,  2rem);
            --space-1:0.25rem; --space-2:0.5rem;  --space-3:0.75rem; --space-4:1rem;
            --space-5:1.25rem; --space-6:1.5rem;  --space-8:2rem;    --space-10:2.5rem;
            --space-12:3rem;   --space-16:4rem;   --space-20:5rem;
            --navy:#0f2744; --red:#c62828; --gold:#d6a23f;
            --ink:#1f2937;  --muted:#6b7280;
            --bg:#f4f6f8;   --card:#ffffff; --line:#d9e0e7; --soft:#eef2f6;
            --radius:20px;  --radius-sm:12px;
            --shadow:0 18px 40px rgba(14,39,68,.10);
            --font-body:'Satoshi', sans-serif;

            /* Logo panel: fluid width clamped between 180px and 500px */
            --logo-w: clamp(180px, 42vw, 500px);
            --logo-h: clamp(90px,  21vw, 250px);
            --logo-img-h: clamp(30px, 6vw, 65px);
            --logo-pl: clamp(20px, 5vw, 60px);
          }

          /* ── Base ── */
          .ems-report {
            font-family: var(--font-body);
            background: linear-gradient(180deg,#eef4f9 0%,#f7fafc 100%);
            color: var(--ink);
            line-height: 1.5;
          }
          .ems-report .report {
            max-width: 1100px;
            margin: 0 auto;
            padding: var(--space-8) var(--space-4) var(--space-16);
          }
          .ems-report .page {
            background: var(--card);
            border: 1px solid var(--line);
            border-radius: 28px;
            box-shadow: var(--shadow);
            overflow: hidden;
            margin-bottom: var(--space-8);
          }

          /* ── Cover ── */
          .ems-report .cover {
            min-height: 92vh;
            position: relative;
            background: radial-gradient(circle at top right, rgba(214,162,63,.22), transparent 28%),
                        linear-gradient(135deg,#A80D2C,#C4163A 55%,#7B0F2B);
            color: white;
          }
          .ems-report .cover-inner {
            padding: var(--space-16);
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            min-height: 92vh;
          }
          .ems-report .brand {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: var(--space-4);
            position: relative;
            z-index: 2;
            padding-right: clamp(0px, 38vw, 200px);
          }
          /* Logo panel */
         .ems-report .logo-panel {
            position: absolute;
            top: 0;
            right: 0;
            width: var(--logo-w);
            height: var(--logo-h);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1;
            pointer-events: none;
          }
          .ems-report .logo-panel-inner {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            position: relative;
            z-index: 2;
            padding-left: var(--logo-pl);
            padding-bottom: clamp(8px, 2vw, 25px);
          }
          .ems-report .logo-img {
            height: var(--logo-img-h);
            width: auto;
            object-fit: contain;
            flex-shrink: 0;
          }
            .ems-report .badge {
            border: 1px solid rgba(255,255,255,.18);
            background: rgba(255,255,255,.08);
            padding: clamp(.3rem,.8vw,.6rem) clamp(.5rem,1.2vw,1rem);
            border-radius: 999px;
            font-size: clamp(0.58rem,1.5vw,0.85rem);
            white-space: nowrap;
            flex-shrink: 0;
          }
          .ems-report .cover-title { margin-top: var(--space-16); }
          .ems-report .cover-title h2 {
            margin: 0 0 var(--space-4);
            font-size: clamp(2.6rem,5vw,4.8rem);
            line-height: .96;
          }
          .ems-report .cover-title p {
            font-size: var(--text-lg);
            max-width: 100%;
            color: rgba(255,255,255,.84);
            text-align: justify;
          }
          .ems-report .cover-grid {
            display: grid;
            grid-template-columns: repeat(3,1fr);
            gap: var(--space-4);
            margin-top: var(--space-12);
          }
          .ems-report .meta {
            background: rgba(255,255,255,.08);
            border: 1px solid rgba(255,255,255,.16);
            border-radius: 18px;
            padding: 1rem 1.1rem;
            font-size: 1.2rem;
          }
          .ems-report .meta span { display: block; font-size: var(--text-xs); color: rgba(255,255,255,.66); margin-bottom: .25rem; }
          .ems-report .meta strong { font-size: var(--text-base); color: white; }
          .ems-report .cover-badges { display: flex; flex-wrap: wrap; gap: .6rem; margin-top: var(--space-8); }
          .ems-report .cover-badge {
            background: rgba(255,255,255,.12);
            border: 1px solid rgba(255,255,255,.18);
            padding: .5rem .9rem;
            border-radius: 999px;
            font-size: 0.95rem;
          }
          .ems-report .footer-note {
            display: flex;
            justify-content: space-between;
            gap: var(--space-4);
            color: rgba(255,255,255,.72);
            font-size: var(--text-xs);
            margin-top: var(--space-10);
          }

          /* ── Inner pages ── */
          .ems-report .page-inner { padding: var(--space-10); }
          .ems-report .topbar {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            gap: var(--space-4);
            margin-bottom: var(--space-8);
          }
          .ems-report .section-kicker {
            color: var(--red);
            font-size: var(--text-xs);
            text-transform: uppercase;
            letter-spacing: .12em;
            font-weight: 700;
          }
          .ems-report .section-title { margin: .35rem 0 0; font-size: var(--text-xl); color: var(--navy); }
          .ems-report .card {
            background: linear-gradient(180deg,#fff 0%,#fbfcfd 100%);
            border: 1px solid var(--line);
            border-radius: var(--radius);
            padding: var(--space-6);
          }
          .ems-report .card h3 { margin: 0 0 .75rem; font-size: var(--text-lg); color: var(--navy); }
          .ems-report .lead { color: #334155; font-size: var(--text-base); margin: 0; text-align: justify; }
          .ems-report .pill {
            display: inline-flex; align-items: center; gap: .45rem;
            padding: .5rem .85rem; border-radius: 999px;
            font-size: var(--text-xs); font-weight: 700;
            text-transform: uppercase; letter-spacing: .06em; white-space: nowrap;
          }
          .ems-report .pill.red { background: #fef2f2; color: var(--red); }

          /* ── Sub-panel (inside a card) ── */
          .ems-report .sub-panel {
            background: linear-gradient(180deg,#fff 0%,#fbfcfd 100%);
            border: 1px solid var(--line);
            border-radius: var(--radius);
            padding: var(--space-5);
          }
          .ems-report .sub-panel h4 { margin: 0 0 .6rem; font-size: var(--text-base); color: var(--navy); }

          /* ── Bars ── */
          .ems-report .bar-row { display: grid; grid-template-columns: 140px 1fr 36px; gap: 10px; align-items: center; margin-bottom: .8rem; }
          .ems-report .bar-label { font-size: .88rem; font-weight: 700; color: var(--navy); }
          .ems-report .track { height: 20px; background: #e9eef3; border-radius: 999px; overflow: hidden; }
          .ems-report .fill  { height: 100%; border-radius: 999px; transition: width .8s cubic-bezier(.4,0,.2,1); }
          .ems-report .score-val { text-align: right; font-size: .88rem; font-weight: 700; }

          /* ── Driver tiles ── */
          .ems-report .driver-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: .8rem; margin-bottom: .85rem; }
          .ems-report .driver-tile { border-radius: 14px; border: 1px solid var(--line); padding: .85rem; background: linear-gradient(180deg,#fff,#f8fbff); }
          .ems-report .driver-tile span   { display: block; font-size: .72rem; letter-spacing: .08em; text-transform: uppercase; color: #5B677A; font-weight: 700; margin-bottom: .2rem; }
          .ems-report .driver-tile strong { font-size: 1.85rem; color: #A80D2C; font-weight: 700; display: block; }

          /* ── Callout ── */
          .ems-report .callout {
            background: linear-gradient(135deg,#fff7f7,#fbeaea);
            border: 1px solid var(--line);
            border-radius: 18px;
            padding: 1.25rem 1.5rem;
            color: #334155;
            text-align: justify;
          }

          /* ── Style cards (page 3) — accordion, full row width ── */
          .ems-report .styles-list { display: flex; flex-direction: column; gap: .85rem; }
          .ems-report .style-item { width: 100%; border-radius: 16px; overflow: hidden; }
          .ems-report .style-item-header {
            display: flex; align-items: center; gap: .85rem;
            padding: 1rem; margin: 0; cursor: pointer; user-select: none;
            background: none; border: none; width: 100%; text-align: left; font: inherit;
          }
          .ems-report .style-item-header:focus-visible { outline: 2px solid var(--navy); outline-offset: 2px; }
          .ems-report .style-score-badge { width: 46px; height: 46px; border-radius: 12px; display: grid; place-items: center; font-size: 1.3rem; font-weight: 800; color: white; flex-shrink: 0; }
          .ems-report .style-item-meta strong { display: block; font-size: .97rem; }
          .ems-report .style-item-meta em     { font-style: italic; font-size: .82rem; color: #5B677A; }
          .ems-report .style-chevron {
            flex-shrink: 0; width: 20px; height: 20px;
            display: grid; place-items: center; transition: transform .25s ease;
            color: #5B677A;
          }
          .ems-report .style-item.open .style-chevron { transform: rotate(180deg); }
          .ems-report .style-item-body {
            max-height: 0; overflow: hidden;
            transition: max-height .3s ease, padding .3s ease, opacity .25s ease;
            opacity: 0; padding: 0 1rem;
          }
          .ems-report .style-item.open .style-item-body {
            max-height: 900px; opacity: 1; padding: 0 1rem 1rem;
          }
          .ems-report .style-item p { margin: 0 0 .3rem; font-size: .88rem; color: #334155; text-align: justify; }
          .ems-report .style-item .exec { color: #5B677A; font-size: .82rem; }

          /* ── Driver cards (page 4) ── */
          .ems-report .driver-card { background: linear-gradient(180deg,#fff 0%,#fbfcfd 100%); border: 1px solid var(--line); border-radius: var(--radius); padding: var(--space-5); }
          .ems-report .driver-card-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: .6rem; }
          .ems-report .driver-card-header h3 { margin: 0; font-size: var(--text-lg); color: var(--navy); }
          .ems-report .driver-big-score { font-size: 2rem; font-weight: 800; color: #A80D2C; }
          .ems-report .driver-card p { margin: 0; font-size: .88rem; color: #334155; line-height: 1.6; text-align: justify; }

          /* ── Two-col ── */
          .ems-report .two-col { display: grid; grid-template-columns: 1fr 1fr; gap: var(--space-6); }

          /* ── Back cover ── */
          .ems-report .back-cover { color: white; }
          .ems-report .back-cover .page-inner { display: flex; flex-direction: column; justify-content: space-between; min-height: 56vh; }
          .ems-report .back-cover-header {
            display: flex; flex-wrap: wrap; justify-content: space-between; align-items: center;
            gap: .5rem;
            padding: clamp(.9rem,2.5vw,1.5rem) clamp(.9rem,3vw,2.5rem);
            border-bottom: 1px solid rgba(255,255,255,.15);
          }
          .ems-report .closing h2 { margin: 0 0 1rem; font-size: clamp(1.4rem,4vw,3.25rem); color: #FFFFFF; }
          .ems-report .closing p  { max-width: 100%; color: rgba(255,255,255,.84); font-size: clamp(.85rem,2vw,1.2rem); text-align: justify; }
          .ems-report .contact-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit,minmax(min(140px,100%),1fr));
            gap: clamp(.4rem,1.2vw,1rem);
            margin-top: var(--space-8);
          }
          .ems-report .contact {
            background: rgba(168,16,38,.1);
            border: 1px solid rgba(255,255,255,.16);
            border-radius: clamp(10px,2vw,16px);
            padding: clamp(.6rem,1.5vw,.9rem);
            min-width: 0;
          }
          .ems-report .contact span   { display: block; color: rgba(255,255,255,.64); font-size: var(--text-xs); margin-bottom: .3rem; }
          .ems-report .contact strong { font-size: clamp(.78rem,1.6vw,.92rem); word-break: break-word; }
          .ems-report .light { color: rgba(255,255,255,.68); }
          .ems-report .small { font-size: var(--text-xs); color: var(--muted); }

          /* ── Summary grid ── */
          .ems-report .summary-grid { display: grid; grid-template-columns: 1fr; gap: 1.5rem; }

          /* ── Collapsible grids ── */
          .ems-report .radar-drivers-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 1.25rem; margin-bottom: 1.25rem; }
          .ems-report .bar-charts-grid    { display: grid; grid-template-columns: 1fr 1fr; gap: 1.25rem; }
          .ems-report .driver-cards-grid  { display: grid; grid-template-columns: repeat(3,1fr); gap: 1rem; margin-bottom: 1.25rem; }

          @media print {
            .ems-report { background: white; }
            .ems-report .report { max-width: none; padding: 0; }
            .ems-report .page { box-shadow: none; border-radius: 0; margin: 0 0 12px; border: 1px solid #ddd; }
            #ems-pdf-btn { display: none !important; }
          }

          /* ── Responsive: Tablet (≤ 768px) ── */
          @media (max-width: 768px) {
            .ems-report .report { padding: 1.25rem .75rem 3rem; }
            .ems-report .page { border-radius: 20px; margin-bottom: 1.25rem; }
            


            .ems-report .cover { min-height: 88vh; }
            .ems-report .cover-inner { padding: 2.25rem; min-height: 88vh; }
            .ems-report .cover-title { margin-top: 2.5rem; }
            .ems-report .cover-title h2 { font-size: clamp(2rem,5vw,3.2rem); }
            .ems-report .cover-grid { gap: .75rem; margin-top: 1.5rem; }
            .ems-report .page-inner { padding: 1.75rem; }
            .ems-report .topbar { flex-wrap: wrap; gap: .5rem; }
            .ems-report .pill { font-size: 10px; padding: .35rem .6rem; }
            .ems-report .bar-row { grid-template-columns: 90px 1fr 36px; gap: .5rem; }
            .ems-report .two-col { grid-template-columns: 1fr; }
            .ems-report .contact-grid { grid-template-columns: repeat(auto-fit,minmax(min(140px,100%),1fr)); }
            .ems-report .driver-grid { grid-template-columns: repeat(3,1fr); }
            .ems-report .radar-drivers-grid { grid-template-columns: 1fr; }
            .ems-report .bar-charts-grid    { grid-template-columns: 1fr; }
            .ems-report .driver-cards-grid  { grid-template-columns: 1fr; }
          }

          /* ── Responsive: Mobile (≤ 480px) ── */
          @media (max-width: 480px) {
            .ems-report .report { padding: .75rem .5rem 2.5rem; }
            .ems-report .page { border-radius: 16px; margin-bottom: .9rem; }
            .ems-report .cover { min-height: 100svh; }
            .ems-report .cover-inner { padding: 1.25rem; min-height: 100svh; }
            
            

            .ems-report .cover-title { margin-top: 1.5rem; }
            .ems-report .cover-title h2 { font-size: clamp(1.6rem,7vw,2.2rem); line-height: 1.08; }
            .ems-report .cover-title p  { font-size: .875rem; }
            .ems-report .cover-grid { grid-template-columns: repeat(auto-fit,minmax(min(130px,100%),1fr)); gap: .5rem; margin-top: 1.1rem; }
            .ems-report .meta { padding: .6rem .75rem; }
            .ems-report .cover-badges { margin-top: .9rem; gap: .4rem; }
            .ems-report .cover-badge { font-size: .7rem; padding: .3rem .55rem; }
            .ems-report .footer-note { flex-direction: column; gap: .2rem; font-size: .68rem; margin-top: 1rem; }
            .ems-report .page-inner { padding: 1rem; }
            .ems-report .topbar { flex-wrap: wrap; gap: .4rem; margin-bottom: 1rem; }
            .ems-report .section-title { font-size: 1.25rem; }
            .ems-report .pill { font-size: 9px; padding: .25rem .5rem; }
            .ems-report .summary-grid { gap: .75rem; }
            .ems-report .card { padding: .875rem; border-radius: 14px; }
            .ems-report .card h3 { font-size: .95rem; margin-bottom: .45rem; }
            .ems-report .lead { font-size: .84rem; }
            .ems-report .bar-row { grid-template-columns: 1fr 28px; gap: .4rem; row-gap: .2rem; }
          .ems-report .bar-row .bar-label { grid-column: 1 / -1; }
            .ems-report .bar-label { font-size: .77rem; }
            .ems-report .track { height: 16px; }
            .ems-report .score-val { font-size: .77rem; }
            .ems-report .style-item { padding: .75rem; border-radius: 12px; }
            .ems-report .style-item p { font-size: .8rem; }
            .ems-report .driver-grid { grid-template-columns: 1fr; }
            .ems-report .callout { padding: .875rem 1rem; border-radius: 14px; }
            .ems-report .radar-drivers-grid { grid-template-columns: 1fr; }
            .ems-report .bar-charts-grid    { grid-template-columns: 1fr; }
            .ems-report .driver-cards-grid  { grid-template-columns: 1fr; }
            .ems-report .closing h2 { font-size: clamp(1.45rem,5.5vw,2rem); }
            .ems-report .closing p  { font-size: .88rem; }
            .ems-report .contact-grid { grid-template-columns: 1fr; gap: .5rem; margin-top: 1rem; }
            .ems-report .two-col { grid-template-columns: 1fr; }
          }

          /* ── Responsive: Tiny (≤ 390px) — covers 360×640 ── */
          @media (max-width: 390px) {
            .ems-report .report { padding: .5rem .35rem 2rem; }
            .ems-report .page { border-radius: 12px; margin-bottom: .75rem; }
            
            

            .ems-report .cover { min-height: 100svh; }
            .ems-report .cover-inner { padding: 1rem; min-height: 100svh; }
            .ems-report .brand { padding-right: clamp(0px,44vw,160px); }
            .ems-report .badge { font-size: .55rem; padding: .25rem .5rem; }
            .ems-report .cover-title { margin-top: 1rem; }
            .ems-report .cover-title h2 { font-size: clamp(1.35rem,6.5vw,1.7rem); line-height: 1.1; }
            .ems-report .cover-title p  { font-size: .78rem; }
            .ems-report .cover-grid { grid-template-columns: 1fr; gap: .4rem; margin-top: .9rem; }
            .ems-report .meta { padding: .5rem .65rem; font-size: 1rem; border-radius: 12px; }
            .ems-report .meta strong { font-size: .82rem; }
            .ems-report .cover-badges { margin-top: .75rem; gap: .3rem; }
            .ems-report .cover-badge { font-size: .65rem; padding: .25rem .45rem; }
            .ems-report .footer-note { flex-direction: column; gap: .15rem; font-size: .62rem; margin-top: .75rem; }
            .ems-report .page-inner { padding: .85rem; }
            .ems-report .bar-row { grid-template-columns: 1fr 24px; gap: .3rem; row-gap: .2rem; }
          .ems-report .bar-row .bar-label { grid-column: 1 / -1; }
            .ems-report .bar-label { font-size: .72rem; }
            .ems-report .track { height: 14px; }
            .ems-report .contact-grid { grid-template-columns: 1fr; gap: .4rem; margin-top: .75rem; }
            .ems-report .driver-grid  { grid-template-columns: 1fr; }
          }

          /* ── Floating PDF button ── */
          #ems-pdf-btn {
            position: fixed;
            bottom: 2rem; right: 2rem;
            z-index: 9999;
            display: inline-flex; align-items: center; gap: .6rem;
            padding: .75rem 1.4rem;
            background: linear-gradient(135deg,#F3AC26,#D79A1E);
            color: white;
            border: 1px solid #F3AC26;
            border-radius: 999px;
            box-shadow: 0 8px 24px rgba(198,40,40,.35), 0 0 14px rgba(243,173,38,.45);
            cursor: pointer;
            font-family: var(--font-body);
            font-size: .9rem; font-weight: 700; letter-spacing: .04em;
            text-transform: uppercase;
            transition: transform .18s ease, box-shadow .18s ease, opacity .18s ease;
          }
          #ems-pdf-btn:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 12px 32px rgba(198,40,40,.45); }
          #ems-pdf-btn:active:not(:disabled) { transform: translateY(0); }
          #ems-pdf-btn:disabled { opacity: .72; cursor: not-allowed; }
          #ems-pdf-btn .btn-spinner {
            width: 16px; height: 16px;
            border: 2px solid rgba(255,255,255,.35);
            border-top-color: white;
            border-radius: 50%;
            animation: spin .7s linear infinite;
            flex-shrink: 0;
          }
          @keyframes spin { to { transform: rotate(360deg); } }
          @keyframes fadeUp { from{opacity:0;transform:translateY(18px)} to{opacity:1;transform:none} }
          .fade-up { animation: fadeUp .55s ease both; }
        `}</style>

        {/* ── Floating PDF Download Button ── */}
        <button
          id="ems-pdf-btn"
          onClick={handleDownloadPDF}
          disabled={loading || isGenerating}
          aria-label="Download PDF Report"
        >
          {isGenerating ? (
            <><span className="btn-spinner" />Generating PDF…</>
          ) : (
            <>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              Download PDF
            </>
          )}
        </button>

        <div className="report">

          {/* ══════════════════════════════════════════
              PAGE 1 — COVER
          ══════════════════════════════════════════ */}
          <section className="page cover">
            <div className="cover-inner">

              {/* Logo panel */}
              <div className="logo-panel">
                <svg
                  width="100%" height="100%"
                  viewBox="0 0 500 250"
                  style={{ position: 'absolute', top: 0, right: 0, zIndex: 0 }}
                  preserveAspectRatio="none"
                >
                  <path d="M 60,8 L 500,8 L 500,188 L 150,253 Q 120,258 113,229 Z" fill="rgba(0,0,0,0.04)" />
                  <path d="M 60,4 L 500,4 L 500,184 L 150,249 Q 120,254 113,225 Z" fill="rgba(0,0,0,0.06)" />
                  <path d="M 60,0 L 500,0 L 500,180 L 150,245 Q 120,250 113,221 Z" fill="white" />
                </svg>
                <div className="logo-panel-inner">
                  <img src={Acumen_25_Years_LOGO} alt="Acumen Logo" className="logo-img" />
                </div>
              </div>

              <div className="brand">
                <div className="badge">Confidential Individual Report</div>
              </div>

              <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", flex: 1 }}>
                <div className="cover-title">
                  <h2>Leadership Styles <br></br> Assessment</h2>
                  <p>
                    A comprehensive leadership profile covering six management styles and three
                    motivational drivers, presented in a consulting-ready format to support
                    targeted development and behavioural growth.
                  </p>
                </div>

                <div className="cover-grid">
                  <div className="meta"><span>Participant</span><strong>{loading ? "—" : (reportMeta?.orgName || "—")}</strong></div>
                  <div className="meta"><span>Assessment Date</span><strong>{loading ? "—" : (reportMeta?.period || "—")}</strong></div>
                  <div className="meta"><span>Primary Style</span><strong>{loading ? "—" : dominantStyleLabel}</strong></div>
                </div>

                <div className="cover-badges">
                  {["Executive Dashboard", "Six Leadership Styles", "Three Motivational Drivers"].map(b => (
                    <span key={b} className="cover-badge">{b}</span>
                  ))}
                </div>
              </div>

              <div className="footer-note">
                <div>Based on Goleman's six leadership styles framework and three motivational drivers.</div>
                <div>Each style scored out of 10.</div>
              </div>
            </div>
          </section>

          {/* ══════════════════════════════════════════
              PAGE 2 — EXECUTIVE DASHBOARD
          ══════════════════════════════════════════ */}
          <section className="page">
            <div className="page-inner">
              <div className="topbar">
                <div>
                  <div className="section-kicker">Page 2</div>
                  <h2 className="section-title">Executive Dashboard</h2>
                </div>
                <div className="pill red">Final adjusted scores</div>
              </div>

              <p className="lead" style={{ marginBottom: "1.25rem" }}>
                This section presents final adjusted scores across the six leadership styles and
                three motivational drivers. Scores reflect dominant and secondary management
                behaviours, each out of a maximum of 10 points.
              </p>

              {/* Radar + Drivers */}
              <div className="radar-drivers-grid">
                <div className="sub-panel">
                  <h4>Your Leadership Styles Breakdown</h4>
                  <RadarChart data={stylesForDisplay} loading={loading} />
                </div>
                <div className="sub-panel">
                  <h4>Your Core Motivational Drivers</h4>
                  <p className="lead" style={{ fontSize: ".88rem", marginBottom: ".85rem" }}>
                    These drivers represent the macro-level themes that energize your management approach.
                  </p>
                  <div className="driver-grid">
                    {driversForDisplay.map(({ def, score }) => (
                      <div className="driver-tile" key={def.key}>
                        <span>{def.label}</span>
                        <strong>{loading ? "—" : (score ?? "—")}</strong>
                      </div>
                    ))}
                  </div>
                  <div className="callout">
                    <strong>Evaluation insight: </strong>
                    {loading ? "Loading…" : insightText}
                  </div>
                </div>
              </div>

              {/* Bar charts */}
              <div className="bar-charts-grid">
                <div className="card">
                  <h3>Style Scores at a Glance <span style={{ fontSize: ".78rem", color: T.slate, fontWeight: 400 }}>— out of 10</span></h3>
                  {stylesForDisplay.map(({ def, score }) => {
                    const sc = STYLE_COLORS[def.key];
                    return (
                      <div className="bar-row" key={def.key}>
                        <div className="bar-label" style={{ color: sc.color }}>{def.label}</div>
                        <div className="track">
                          <div className="fill" style={{ width: `${((score ?? 0) / 10) * 100}%`, background: sc.gradient }} />
                        </div>
                        <div className="score-val" style={{ color: sc.color }}>{loading ? "—" : (score ?? "—")}</div>
                      </div>
                    );
                  })}
                </div>
                <div className="card">
                  <h3>Motivational Driver Spread <span style={{ fontSize: ".78rem", color: T.slate, fontWeight: 400 }}>— raw scores</span></h3>
                  {driversForDisplay.map(({ def, score }) => (
                    <div className="bar-row" key={def.key}>
                      <div className="bar-label">{def.label}</div>
                      <div className="track">
                        <div className="fill" style={{ width: `${((score ?? 0) / maxDriver) * 100}%`, background: `linear-gradient(90deg,${T.navy},${T.blue},${T.gold})` }} />
                      </div>
                      <div className="score-val" style={{ color: T.navy }}>{loading ? "—" : (score ?? "—")}</div>
                    </div>
                  ))}
                  <div style={{ marginTop: "1rem", padding: ".85rem", background: "#f8f4eb", borderRadius: "14px", border: `1px solid ${T.gold}44` }}>
                    <div style={{ fontSize: ".75rem", color: T.gold, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", marginBottom: ".3rem" }}>Dominant Profile</div>
                    <div style={{ fontSize: ".9rem", color: T.ink }}>
                      Style: <strong style={{ color: T.navy }}>{dominantStyleLabel}</strong>{" "}(secondary: <strong>{secondStyleLabel}</strong>)
                    </div>
                    <div style={{ fontSize: ".9rem", color: T.ink, marginTop: ".25rem" }}>
                      Driver: <strong style={{ color: T.navy }}>{dominantDriverLabel}</strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* ══════════════════════════════════════════
              PAGE 3 — SIX LEADERSHIP STYLES
          ══════════════════════════════════════════ */}
          <section className="page">
            <div className="page-inner">
              <div className="topbar">
                <div>
                  <div className="section-kicker">Page 3</div>
                  <h2 className="section-title">The Six Leadership Styles</h2>
                </div>
                <div className="pill red">Scored out of 10 per style</div>
              </div>

              <p className="lead" style={{ marginBottom: "1.25rem" }}>
                Effective leaders adapt their approach based on the situation, the people involved,
                and the outcomes they want to achieve. The six styles below represent your full
                leadership repertoire.
              </p>

              <div className="styles-list">
                {/* Full-width, ranked highest score → lowest, each an expandable dropdown */}
                {sortedStyles.map(({ def, score }) => {
                  const sc = STYLE_COLORS[def.key];
                  const isDominant = def.label === dominantStyleLabel;
                  const effectiveOpenKey = openStyleKey ?? sortedStyles[0]?.def?.key;
                  const isOpen = def.key === effectiveOpenKey;
                  return (
                    <div
                      key={def.key}
                      className={`style-item${isOpen ? " open" : ""}`}
                      style={{
                        borderTop:    isDominant ? `1.5px solid ${sc.color}` : `1px solid ${T.line}`,
                        borderRight:  isDominant ? `1.5px solid ${sc.color}` : `1px solid ${T.line}`,
                        borderBottom: isDominant ? `1.5px solid ${sc.color}` : `1px solid ${T.line}`,
                        borderLeft:   `5px solid ${sc.color}`,
                        background: `linear-gradient(135deg,${sc.bg} 0%,#ffffff 60%)`,
                      }}
                    >
                      <button
                        type="button"
                        className="style-item-header"
                        aria-expanded={isOpen}
                        onClick={() => setOpenStyleKey(isOpen ? "__none__" : def.key)}
                      >
                        <div className="style-score-badge" style={{ background: sc.gradient }}>{loading ? "—" : (score ?? "—")}</div>
                        <div className="style-item-meta">
                          <strong style={{ color: sc.color }}>{def.label}</strong>
                          <em>{def.tagline}</em>
                        </div>
                        {isDominant && (
                          <div style={{ background: sc.color, color: "#fff", fontSize: "10px", fontWeight: 800, padding: "3px 10px", borderRadius: "999px", textTransform: "uppercase", letterSpacing: ".06em", whiteSpace: "nowrap", marginLeft: "auto" }}>
                            ★ Primary Style
                          </div>
                        )}
                        <svg className="style-chevron" style={{ marginLeft: isDominant ? 0 : "auto" }} viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="6 9 12 15 18 9" />
                        </svg>
                      </button>
                      <div className="style-item-body">
                        <p>{def.overview}</p>
                        {def.execFrame && <p className="exec">{def.execFrame}</p>}
                        <p><strong>Strengths:</strong> {def.strengths}</p>
                        <p><strong>Weaknesses:</strong> {def.weaknesses}</p>
                        <p><strong>Best used when:</strong> {def.bestUsed}</p>
                      </div>
                    </div>
                  );
                })}

                {/* Context box */}
                <div style={{ background: "#f8f4eb", border: `1px solid ${T.gold}55`, borderRadius: "16px", padding: "1rem", marginTop: ".25rem" }}>
                  <div style={{ fontSize: ".72rem", color: T.gold, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".1em", marginBottom: ".5rem" }}>Context &amp; Meaning</div>
                  <p style={{ margin: 0, fontSize: ".88rem", color: T.ink, lineHeight: 1.6, textAlign: "justify" }}>
                    Rather than being fixed traits, these styles are most effective when used
                    situationally, allowing leaders to respond flexibly to changing contexts,
                    team maturity, and organizational climate.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* ══════════════════════════════════════════
              PAGE 4 — MOTIVATIONAL DRIVERS & INTEGRATION
          ══════════════════════════════════════════ */}
          <section className="page">
            <div className="page-inner">
              <div className="topbar">
                <div>
                  <div className="section-kicker">Page 4</div>
                  <h2 className="section-title">Motivational Drivers &amp; Integration</h2>
                </div>
                <div className="pill red">Power · Affiliation · Achievement</div>
              </div>

              {/* Driver cards */}
              <div className="driver-cards-grid">
                {driversForDisplay.map(({ def, score }) => (
                  <div className="driver-card" key={def.key}>
                    <div className="driver-card-header">
                      <h3>{def.label}</h3>
                      <span className="driver-big-score">{loading ? "—" : (score ?? "—")}</span>
                    </div>
                    <p>{def.desc}</p>
                  </div>
                ))}
              </div>

              {/* How styles work together */}
              <div className="card" style={{ marginBottom: "1.1rem" }}>
                <h3>How the Styles Work Together</h3>
                <p className="lead">
                  The strongest leaders do not rely on only one style. They shift between styles
                  depending on the challenge, the maturity of the team, and the emotional climate.
                  The framework is best used as a development lens that helps leaders reflect on
                  their dominant tendencies while building flexibility across all six styles.
                </p>
                <div className="callout" style={{ marginTop: ".85rem" }}>
                  <strong>Evaluation insight: </strong>
                  {loading ? "Loading…" : (insightText || "Flexibility across styles — informed by strong self-awareness — is the hallmark of sustained executive effectiveness.")}
                </div>
              </div>

              {/* Two-col */}
              <div className="two-col">
                <div className="card">
                  <h3>Understanding Your Scores</h3>
                  <p className="lead">
                    Daniel Goleman's research on emotional intelligence shows that leadership is not
                    only about direction and control, but also about climate, relationships, and
                    performance. The six styles work as a repertoire that leaders can use flexibly —
                    some build engagement and trust, while others create urgency and discipline when
                    the situation demands it.
                  </p>
                </div>
                <div className="card">
                  <h3>Coaching Note</h3>
                  <p className="lead">
                    This report is most powerful when reviewed in a guided debrief with a manager,
                    coach, or facilitator. The goal is not to determine where you stand today, but to
                    identify which behaviours to strengthen and which combinations to deploy more
                    deliberately in your leadership practice.
                  </p>
                </div>
              </div>

              {/* Error state */}
              {error && (
                <div style={{ background: "#fff0f0", border: "1px solid #f5c6c6", borderRadius: 12, padding: "14px 18px", color: "#9b1c1c", fontSize: ".93rem", marginTop: "1rem" }}>
                  ⚠️ {error}
                </div>
              )}
            </div>
          </section>

          {/* ══════════════════════════════════════════
              BACK COVER
          ══════════════════════════════════════════ */}
          <section className="page back-cover">
            <div style={{
              background: `radial-gradient(circle at top right, rgba(214,162,63,.22), transparent 28%), linear-gradient(135deg,#A80D2C,#C4163A 55%,#7B0F2B)`,
              minHeight: "56vh",
            }}>
              <div className="back-cover-header">
                <div style={{ fontSize: ".72rem", textTransform: "uppercase", letterSpacing: ".12em", fontWeight: 700, color: "rgba(255,255,255,0.66)" }}>
                  {/* Back Cover */}
                  </div>
                <div style={{ fontSize: ".72rem", color: "rgba(255,255,255,0.5)", letterSpacing: ".05em" }}>Acumen Business Catalyst</div>
              </div>

              <div className="page-inner">
                <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", flex: 1 }}>
                  <div className="closing">
                    <h2>Building Flexible, Adaptive Leadership</h2>
                    <p>
                      Leadership style is not fixed. It is a repertoire that expands through
                      self-awareness, deliberate practice, and consistent reflection. Acumen Business
                      Catalyst uses assessments like this to turn behavioural data into focused
                      development conversations that drive lasting change.
                    </p>
                  </div>
                </div>

                <div>
                  <div className="card" style={{ marginTop: "1.5rem", background: "rgba(255,255,255,.06)", borderColor: "rgba(255,255,255,.14)", color: "white" }}>
                    <h3 style={{ color: "white" }}>Why Style Flexibility Matters</h3>
                    <p className="lead" style={{ color: "rgba(255,255,255,.84)" }}>
                      Research on executive effectiveness consistently shows that leaders who
                      consciously shift between styles — depending on team readiness, task complexity,
                      and organizational context — produce stronger outcomes and healthier cultures
                      than those who rely on a single dominant approach.
                    </p>
                  </div>

                  <div className="contact-grid">
                    <div className="contact"><span>Brand</span><strong>Acumen Business Catalyst</strong></div>
                    <div className="contact"><span>Focus</span><strong>Leadership • Culture • Capability</strong></div>
                    <div className="contact"><span>Report type</span><strong>Individual Leadership Profile</strong></div>
                  </div>

                  <p className="light" style={{ marginTop: "1.5rem", fontSize: ".75rem" }}>
                    This report is generated from the Executive Management Styles (EMS) assessment framework. © 2026 Acumen Business Catalyst.
                  </p>
                </div>
              </div>
            </div>
          </section>

        </div>
      </div>

      {/* ── Hidden PDF generator ── */}
      {pdfData && (
        <HiddenEMSReport
          participantData={pdfData}
          onComplete={handlePdfComplete}
        />
      )}
    </>
  );
}